<template>
  <div class="policy-page flex flex-col justify-center items-center p-16 gap-8 w-full">
    <div class="row flex gap-8 flex-wrap w-full justify-center">
      <div class="section p-8 flex flex-col gap-4 w-66">
        <h1 class="text-4xl my-4">Privacy Policy</h1>
        <p class="effective-date"><em>Last updated: October 9, 2026</em></p>

        <p>
          This Privacy Policy explains how <strong>Astro Path</strong> (the "App"), a
          mobile puzzle game developed by Alexandros Ploumis ("we", "us",
          "our"), collects, uses, and protects information. Please also read our
          <router-link to="/terms-of-use">Terms of Use</router-link>.
        </p>

        <h2>1. Information We Collect</h2>

        <h3>Collected automatically by services we use</h3>
        <ul>
          <li>
            <strong>Advertising (Google AdMob):</strong> unless you have bought
            "Remove Ads", AdMob may collect your device's advertising ID, device
            and app information, approximate location derived from your IP
            address, and ad interactions, to show and measure ads. Where
            required (for example in the EU, EEA and UK), you are asked for
            consent before personalised ads are shown, and you can change your
            choice later.
          </li>
          <li>
            <strong>Analytics (Firebase Analytics):</strong> how the App is used
            (for example levels played and screens opened), app version, device
            model and operating system, and an app-instance identifier.
          </li>
          <li>
            <strong>Crash reporting (Firebase Crashlytics):</strong> crash logs
            and the state of your device when the App crashes, so we can fix
            bugs.
          </li>
          <li>
            <strong>Purchases (Google Play):</strong> if you buy "Remove Ads",
            payment is handled entirely by Google Play. We only receive
            confirmation that the purchase was made; we never see your payment
            details.
          </li>
        </ul>

        <h3>Daily Puzzle (only if you use it)</h3>
        <p>
          The Daily Puzzle is optional. When you first open it, an anonymous
          account is created for you automatically (Firebase Authentication)
          and you choose a username. We do not ask for your name, email
          address, phone number, or password. We store:
        </p>
        <ul>
          <li>an anonymous account ID;</li>
          <li>the username you choose and your friend code;</li>
          <li>your friends list (the players you have added, and who added you);</li>
          <li>your daily puzzle results: the date and your solve time;</li>
          <li>basic stats: your current streak, how many daily puzzles you have
            completed, and the date you last played;</li>
          <li>a server timestamp used to keep the daily puzzle in sync with the
            correct date;</li>
          <li>reports you submit about another player's username (their account
            ID and username, the date, and your account ID).</li>
        </ul>

        <h3>Stored only on your device</h3>
        <p>
          Your level progress, coins, settings, and unlocks are stored on your
          device only and are never sent to us.
        </p>

        <h2>2. How We Use Information</h2>
        <ul>
          <li>to run the Daily Puzzle: leaderboards, ranks, friends, and streaks;</li>
          <li>to show ads, which fund the free version of the App;</li>
          <li>to understand how the App is used and to fix crashes and bugs;</li>
          <li>to process your "Remove Ads" purchase;</li>
          <li>to review reports and keep usernames and leaderboards fair.</li>
        </ul>
        <p>
          We rely on your consent for personalised advertising, on providing the
          service you ask for when you use the Daily Puzzle, and on our
          legitimate interest in keeping the App working and secure.
        </p>

        <h2>3. What Other Players Can See</h2>
        <p>
          Your username and your daily puzzle results (solve time and rank) are
          visible to all players on the leaderboards. Players you are friends
          with see you on their friends list and friends leaderboard. Your
          friend code is shown to you so you can share it; anyone you give it to
          can add you as a friend.
        </p>

        <h2>4. Sharing</h2>
        <p>
          We do not sell your information. Data is processed on our behalf by
          Google (Firebase, AdMob, and Google Play), which act as our service
          providers under their own terms. You can read more in
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Google's Privacy Policy</a>,
          <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener">Firebase's privacy information</a>,
          and
          <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener">how Google uses data from apps that use its services</a>.
        </p>

        <h2>5. Storage and Security</h2>
        <p>
          Daily Puzzle data is stored on Google Firebase servers. All data is
          encrypted in transit. Access is restricted by security rules so that
          only you can change your own data.
        </p>

        <h2>6. How Long We Keep It</h2>
        <p>
          Daily Puzzle data is kept until you delete it, or until we discontinue
          the feature. Reports are kept as long as needed for moderation.
          Analytics, crash, and advertising data are kept according to Google's
          retention policies.
        </p>

        <h2 id="delete-data">7. Delete Your Data</h2>
        <p>You can delete all of your Astro Path online data at any time.</p>
        <p>
          <strong>In the App:</strong> open <strong>Daily Puzzle</strong>, tap
          <strong>Delete my online data</strong>, and confirm. Deletion happens
          immediately.
        </p>
        <p>
          <strong>Without the App:</strong> email
          <a href="mailto:ploumisalexandrosgr@gmail.com?subject=Astro%20Path%20-%20Delete%20my%20data">ploumisalexandrosgr@gmail.com</a>
          with the subject "Astro Path - Delete my data", and include your friend
          code (shown on the Daily Puzzle screen) if you have it. We will delete
          your data within 30 days.
        </p>
        <p>
          <strong>What is deleted:</strong> your anonymous account, username,
          friend code, friends list (on both sides), daily puzzle results and
          leaderboard entries, stats, and server timestamp.
        </p>
        <p>
          <strong>What is not affected:</strong> progress stored only on your
          device (levels, coins, settings) stays on your device. Reports you
          made about other players are kept for moderation; after deletion they
          no longer link to any profile. Analytics, crash, and advertising data
          are handled by Google under its own policies.
        </p>

        <h2>8. Your Rights</h2>
        <p>
          Depending on where you live (including in the EU and EEA under the
          GDPR), you may have the right to access, correct, or delete your data,
          to object to or restrict its processing, and to withdraw consent at
          any time. To exercise these rights, contact us at the address below.
          You can also lodge a complaint with your local data protection
          authority; in Greece this is the Hellenic Data Protection Authority.
        </p>

        <h2>9. Children</h2>
        <p>
          The App is not directed at children under 13, and we do not knowingly
          collect personal information from them. If you believe a child has
          provided us with data, contact us and we will delete it.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. The "Last
          updated" date above shows when it was last changed. Continued use of
          the App after a change means you accept the updated policy.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          Questions about this Privacy Policy or your data can be sent to
          <a href="mailto:ploumisalexandrosgr@gmail.com">ploumisalexandrosgr@gmail.com</a>.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy'
};
</script>

<style scoped>
  h1 {
    margin-bottom: 0;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: bold;
    margin-top: 0.5rem;
    scroll-margin-top: 1rem;
  }

  h3 {
    font-weight: bold;
    margin-top: 0.25rem;
  }

  ul {
    list-style: disc;
    padding-left: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .effective-date {
    opacity: 0.7;
  }

  .w-66 {
    width: 66%;
  }

  @media only screen and (max-width: 600px) {
    .policy-page.p-16 {
      padding: 1rem;
    }

    .w-66 {
      width: 100%;
    }
  }
</style>
